import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface SlideInfo {
  id: string;
  file: string;
  title: string;
  position?: number;
  thumbnail?: string;
  // Per-slide overrides (optional — falls back to global config)
  interval?: number;
  transition?: TransitionType;
  transitionDuration?: number;
}

export type TransitionType =
  | "fade"
  | "dissolve"
  | "slide"
  | "slide-up"
  | "slide-down"
  | "zoom"
  | "wipe"
  | "wipe-up"
  | "wipe-down"
  | "wipe-tl"
  | "wipe-tr"
  | "wipe-bl"
  | "wipe-br"
  | "flip"
  | "cube"
  | "none";

export interface PresentationConfig {
  autoplay: boolean;
  interval: number;
  loop: boolean;
  transition: TransitionType;
  transitionDuration: number;
  showControls: boolean;
  pauseOnInteraction: boolean;
  resumeDelay: number;
}

const DEFAULT_PRESENTATION_CONFIG: PresentationConfig = {
  autoplay: false,
  interval: 5000,
  loop: false,
  transition: "fade",
  transitionDuration: 300,
  showControls: true,
  pauseOnInteraction: true,
  resumeDelay: 10000,
};

interface SlideState {
  slides: SlideInfo[];
  currentSlideIndex: number;
  totalSlides: number;
  isSlideProject: boolean;
  isSidebarCollapsed: boolean;
  isExporting: boolean;
  exportProgress: number;
  thumbnailVersion: number;
  presentationConfig: PresentationConfig;

  setSlides: (slides: SlideInfo[]) => void;
  setPresentationConfig: (config: Partial<PresentationConfig>) => void;
  updateSlideConfig: (slideId: string, config: { interval?: number | null; transition?: TransitionType | null; transitionDuration?: number | null }) => void;
  setCurrentSlide: (index: number) => void;
  setTotalSlides: (total: number) => void;
  setIsSlideProject: (value: boolean) => void;
  setSidebarCollapsed: (value: boolean) => void;
  setIsExporting: (value: boolean) => void;
  setExportProgress: (value: number) => void;
  bumpThumbnailVersion: () => void;
  reset: () => void;
}

export const useSlideStore = create<SlideState>()(
  devtools(
    (set) => ({
      slides: [],
      currentSlideIndex: 0,
      totalSlides: 0,
      isSlideProject: false,
      isSidebarCollapsed: false,
      isExporting: false,
      exportProgress: 0,
      thumbnailVersion: 0,
      presentationConfig: { ...DEFAULT_PRESENTATION_CONFIG },

      setSlides: (slides) => set({ slides, totalSlides: slides.length }),
      setPresentationConfig: (config) =>
        set((s) => ({
          presentationConfig: { ...s.presentationConfig, ...config },
        })),
      updateSlideConfig: (slideId, config) =>
        set((s) => ({
          slides: s.slides.map((slide) => {
            if (slide.id !== slideId) return slide;
            const updated = { ...slide };
            // null = clear override, undefined = no change, value = set override
            for (const [key, val] of Object.entries(config)) {
              if (val === null) {
                delete updated[key as keyof typeof updated];
              } else if (val !== undefined) {
                (updated as Record<string, unknown>)[key] = val;
              }
            }
            return updated;
          }),
        })),
      setCurrentSlide: (index) => set({ currentSlideIndex: index }),
      setTotalSlides: (total) => set({ totalSlides: total }),
      setIsSlideProject: (value) => set({ isSlideProject: value }),
      setSidebarCollapsed: (value) => set({ isSidebarCollapsed: value }),
      setIsExporting: (value) => set({ isExporting: value }),
      setExportProgress: (value) => set({ exportProgress: value }),
      bumpThumbnailVersion: () =>
        set((s) => ({ thumbnailVersion: s.thumbnailVersion + 1 })),
      reset: () =>
        set({
          slides: [],
          currentSlideIndex: 0,
          totalSlides: 0,
          isSlideProject: false,
          isSidebarCollapsed: false,
          isExporting: false,
          exportProgress: 0,
          thumbnailVersion: 0,
          presentationConfig: { ...DEFAULT_PRESENTATION_CONFIG },
        }),
    }),
    { name: "slide-store" },
  ),
);
